var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Alert, } from "@mui/material";
import { Serializer } from "survey-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AUTOCOMPLETE_VALUES } from "../../utils/autocomplete-values";
import FormStepsContent from "../../components/shared/FormStepsContent";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { useWebgate } from "@xvia/webgate-connect";
import { useAccessTokenRef } from "../../hooks/useAccessTokenRef";
import { FORM_STEPS } from "../../constants/forms.constants";
import { buildBaseFormPayload, focusFormInputAfterValidationError, formHasPages, getApiErrorMessage, getFormValidationError, defineAutoCompleteOptions, forceSyncJsonEditorTab, } from "../../utils/forms.functions";
import FormStepper from "../shared/FormStepper";
import { getDefaultSlugFromSlugs } from "../../utils/helpers";
var DesignerLayout = function (_a) {
    var config = _a.config;
    var navigate = useNavigate();
    var searchParams = useSearchParams()[0];
    var copyFormSlug = searchParams.get("copyForm");
    var userInfo = useWebgate().userInfo;
    var _b = useAccessTokenRef(), accessTokenRef = _b.accessTokenRef, hasAccessToken = _b.hasAccessToken;
    defineAutoCompleteOptions(Serializer, AUTOCOMPLETE_VALUES);
    var creator = useMemo(function () { return config.createCreator(); }, [config]);
    var _c = useState(0), activeStep = _c[0], setActiveStep = _c[1];
    var _d = useState({
        title: "",
        slug: "",
        description: null,
        callbackURL: null,
        callbackIncludeUserAuth: false,
        authRequired: config.defaultAuthRequired,
    }), preFormInfo = _d[0], setPreFormInfo = _d[1];
    var _e = useState(false), open = _e[0], setOpen = _e[1];
    var _f = useState(null), formSlug = _f[0], setFormSlug = _f[1];
    var _g = useState(false), copyLoading = _g[0], setCopyLoading = _g[1];
    var _h = useState(null), copyError = _h[0], setCopyError = _h[1];
    var formTitleInputRef = useRef(null);
    var formSlugInputRef = useRef(null);
    var isLastPage = useMemo(function () { return activeStep === FORM_STEPS.length - 1; }, [activeStep]);
    useEffect(function () {
        var token = accessTokenRef.current;
        if (!copyFormSlug || !token)
            return;
        var cancelled = false;
        setCopyLoading(true);
        setCopyError(null);
        config
            .fetchApi(copyFormSlug, token)
            .then(function (res) {
            var _a;
            if (cancelled)
                return;
            var schema = (_a = res.data.schema) !== null && _a !== void 0 ? _a : {};
            creator.text = JSON.stringify(schema);
        })
            .catch(function (err) {
            if (cancelled)
                return;
            setCopyError(getApiErrorMessage(err, "Não foi possível carregar o formulário original para duplicação."));
        })
            .finally(function () {
            if (!cancelled)
                setCopyLoading(false);
        });
        return function () {
            cancelled = true;
        };
    }, [copyFormSlug, hasAccessToken, creator, config, accessTokenRef]);
    var handleActionsAfterErrorDialogClose = useCallback(function () {
        var _a, _b;
        if (activeStep !== 1)
            return;
        if (!((_a = preFormInfo.title) === null || _a === void 0 ? void 0 : _a.trim())) {
            focusFormInputAfterValidationError(formTitleInputRef);
        }
        else if (!((_b = preFormInfo.slug) === null || _b === void 0 ? void 0 : _b.trim())) {
            focusFormInputAfterValidationError(formSlugInputRef);
        }
    }, [preFormInfo.title, preFormInfo.slug, activeStep]);
    var _j = useErrorDialog(handleActionsAfterErrorDialogClose), errorData = _j.errorData, showError = _j.showError, closeError = _j.closeError;
    var handleNext = useCallback(function () {
        forceSyncJsonEditorTab(creator);
        if (!formHasPages(creator)) {
            showError("O formulário deve ter pelo menos um campo !");
            return;
        }
        setActiveStep(function (prevActiveStep) { return prevActiveStep + 1; });
    }, [creator, showError]);
    var handleBack = useCallback(function () {
        setActiveStep(function (prevActiveStep) { return prevActiveStep - 1; });
    }, []);
    var handleCancel = useCallback(function () {
        navigate(config.listPath);
    }, [navigate, config.listPath]);
    var handleGoToListFromSuccess = useCallback(function () {
        setOpen(false);
        navigate(config.listPath);
    }, [navigate, config.listPath]);
    var submitNewForms = useCallback(function () {
        var validationError = getFormValidationError(preFormInfo, creator);
        if (validationError) {
            showError(validationError);
            return;
        }
        var formBody = __assign(__assign({}, buildBaseFormPayload(preFormInfo, creator)), { userCreated: (userInfo === null || userInfo === void 0 ? void 0 : userInfo.preferred_username) || (userInfo === null || userInfo === void 0 ? void 0 : userInfo.name) || "Sem dados" });
        config
            .createApi(formBody, accessTokenRef.current)
            .then(function (response) {
            var data = response.data;
            setFormSlug(getDefaultSlugFromSlugs(data.slugs));
            setOpen(true);
        })
            .catch(function (err) {
            console.error("Erro ao salvar o formulário", err);
            showError(getApiErrorMessage(err, "Erro ao salvar o formulário. Tente novamente."));
        });
    }, [preFormInfo, creator, accessTokenRef, userInfo, config, showError]);
    var finalFormCreatedUrl = "".concat(process.env.PORTAL_URL).concat(config.urlPrefix).concat(formSlug);
    var handleOpenFormAndClose = function () {
        window.open(finalFormCreatedUrl, "_blank", "noopener,noreferrer");
        handleClose();
    };
    var handleClose = function () {
        setOpen(false);
        window.location.reload();
    };
    if (copyLoading) {
        return (_jsx(Box, __assign({ sx: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 400,
            } }, { children: _jsx(CircularProgress, { size: 40, thickness: 4 }) })));
    }
    return (_jsxs(Grid, __assign({ container: true, sx: { maxWidth: "1920px", margin: "0 auto" } }, { children: [copyError && (_jsx(Grid, __assign({ item: true, xs: 12 }, { children: _jsxs(Alert, __assign({ severity: "warning", onClose: function () { return setCopyError(null); }, sx: { mb: 2 } }, { children: [copyError, " O formul\u00E1rio foi aberto sem a estrutura pr\u00E9-preenchida."] })) }))), _jsx(FormStepper, { activeStep: activeStep }), _jsx(Grid, __assign({ item: true, xs: 12 }, { children: _jsx(FormStepsContent, { step: activeStep, creator: creator, preFormInfo: preFormInfo, setPreFormInfo: setPreFormInfo, autoSyncSlugFromTitle: true, formTitleInputRef: formTitleInputRef, formSlugInputRef: formSlugInputRef, urlPrefix: config.urlPrefix }) })), _jsx(Grid, __assign({ item: true, xs: 12 }, { children: _jsxs(Box, __assign({ sx: {
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 3,
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                    } }, { children: [_jsxs(Box, __assign({ sx: {
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                                flexWrap: "wrap",
                            } }, { children: [activeStep === 0 && (_jsx(Button, __assign({ variant: "contained", color: "error", onClick: handleCancel }, { children: "Cancelar" }))), activeStep > 0 && (_jsx(Button, __assign({ disabled: activeStep === 0, onClick: handleBack, variant: "outlined" }, { children: "Voltar" })))] })), _jsx(Box, { children: isLastPage ? (_jsx(Button, __assign({ variant: "contained", color: "primary", onClick: submitNewForms }, { children: "Salvar Formul\u00E1rio" }))) : (_jsx(Button, __assign({ variant: "contained", color: "primary", onClick: handleNext }, { children: "Continuar" }))) })] })) })), _jsxs(Dialog, __assign({ open: open, onClose: handleClose }, { children: [_jsx(DialogTitle, { children: "Formul\u00E1rio Criado" }), _jsxs(DialogContent, { children: ["O formul\u00E1rio se encontra em: ", _jsx("br", {}), _jsx(Box, __assign({ component: "a", href: finalFormCreatedUrl, target: "_blank", rel: "noopener noreferrer", onClick: function (e) {
                                    e.preventDefault();
                                    handleOpenFormAndClose();
                                }, sx: {
                                    fontWeight: "bold",
                                    color: "primary.main",
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    "&:hover": { textDecoration: "underline" },
                                } }, { children: finalFormCreatedUrl }))] }), _jsxs(DialogActions, { children: [_jsx(Button, __assign({ onClick: handleOpenFormAndClose, color: "primary", variant: "contained" }, { children: "Abrir formul\u00E1rio" })), _jsx(Button, __assign({ onClick: handleGoToListFromSuccess, color: "primary", variant: "outlined" }, { children: "Ir para listagem" })), _jsx(Button, __assign({ onClick: handleClose, color: "primary" }, { children: "Fechar" }))] })] })), _jsxs(Dialog, __assign({ open: errorData.open, onClose: closeError }, { children: [_jsx(DialogTitle, __assign({ color: "blue" }, { children: "Aten\u00E7\u00E3o" })), _jsx(DialogContent, { children: errorData.message }), _jsx(DialogActions, { children: _jsx(Button, __assign({ onClick: closeError, color: "primary" }, { children: "Fechar" })) })] }))] })));
};
export default DesignerLayout;
